import React, { useEffect, useRef } from 'react';
import { linkStyleToCss } from './SiteHeaderLinkStyleField';
import styles from './SiteHeaderPageBar.module.css';

export const pageNumberLabel = (index) => String(index + 1).padStart(2, '0');

/**
 * Défilement horizontal qui amène la case active dans la barre, ou `null` si
 * elle y est déjà entière.
 */
export function scrollLeftToReveal(bar, tab) {
  const tabRight = tab.offsetLeft + tab.offsetWidth;
  if (tab.offsetLeft < bar.scrollLeft) return tab.offsetLeft;
  if (tabRight > bar.scrollLeft + bar.clientWidth) return tabRight - bar.clientWidth;
  return null;
}

/**
 * Barre des pages du Menu : une case par page, dans l'ordre du site. Elle vit
 * dans l'en-tête, rendu une seule fois pour tout le site : changer de page ne
 * la remonte pas, sa position de défilement reste. La page affichée est
 * ramenée dans la vue quand elle change.
 */
const SiteHeaderPageBar = ({
  links,
  activeSlug,
  showNumbers = true,
  activeBackground = '',
  activeColor = '',
  numberColor = '',
  cellBorders = true,
  underline = true,
  textStyle = null,
  isEditing = false,
}) => {
  const barRef = useRef(null);
  const activeRef = useRef(null);
  // Dans l'éditeur aucune page n'est affichée : la première en tient lieu.
  const shownSlug = isEditing ? links[0]?.slug : activeSlug;

  useEffect(() => {
    if (!barRef.current || !activeRef.current) return;
    const next = scrollLeftToReveal(barRef.current, activeRef.current);
    if (next !== null) barRef.current.scrollLeft = next;
  }, [shownSlug]);

  if (!links.length) return null;

  const barStyle = {
    '--page-bar-active-bg': activeBackground || 'transparent',
    '--page-bar-active-color': activeColor || 'var(--mantine-primary-color-filled)',
    '--page-bar-number-color': numberColor || activeColor || 'var(--mantine-primary-color-filled)',
  };
  const tabStyle = linkStyleToCss(textStyle);

  return (
    <nav
      ref={barRef}
      className={[
        styles.bar,
        cellBorders && styles.withBorders,
        underline && styles.underline,
      ].filter(Boolean).join(' ')}
      aria-label="Pages du site"
      style={barStyle}
    >
      {links.map((link, index) => {
        const isActive = link.slug === shownSlug;
        return (
          <a
            key={link.slug}
            ref={isActive ? activeRef : undefined}
            href={`#${link.slug}`}
            className={`${styles.tab} ${isActive ? styles.active : ''}`.trim()}
            style={isActive ? { ...tabStyle, color: undefined } : tabStyle}
            aria-current={isActive ? 'page' : undefined}
            onClick={isEditing ? (event) => event.preventDefault() : undefined}
          >
            {showNumbers && <span className={styles.number}>{pageNumberLabel(index)}</span>}
            <span>{link.title}</span>
          </a>
        );
      })}
    </nav>
  );
};

export default SiteHeaderPageBar;
